// Almacenamiento local IndexedDB — modelo simple alineado con la landing.
//
// Cifrado con deviceKey = Argon2id(devicePin, deviceSalt). Si el dispositivo se pierde,
// el atacante necesita el PIN para descifrar la outbox y los snapshots locales.
// Si el PIN se olvida, recuperación con BIP39 + login normal — la priv de la cuenta
// vive en el server (cifrada con Argon2id del password).

import sodium from 'libsodium-wrappers-sumo';
import * as crypto from '../crypto';

const DB_NAME = 'syphrax-v2';
const DB_VERSION = 1;
const STORE_KV = 'kv';                // {key, value} — meta + snapshots cifrados
const STORE_RATCHET = 'ratchet';      // legacy v115 (Double Ratchet) — drained on first open
const STORE_OUTBOX = 'outbox';        // {id, ...entry} — pendientes de envío
const STORE_INBOX_CURSOR = 'cursors'; // {key, since} — cursor del inbox

let dbPromise: Promise<IDBDatabase> | null = null;
let deviceKeyRef: Uint8Array | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_KV)) db.createObjectStore(STORE_KV);
      if (!db.objectStoreNames.contains(STORE_RATCHET)) db.createObjectStore(STORE_RATCHET);
      if (!db.objectStoreNames.contains(STORE_OUTBOX)) {
        const s = db.createObjectStore(STORE_OUTBOX, { keyPath: 'id' });
        s.createIndex('status', 'status', { unique: false });
        s.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_INBOX_CURSOR)) db.createObjectStore(STORE_INBOX_CURSOR);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function withStore<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const tx = db.transaction(store, mode);
    const s = tx.objectStore(store);
    const req = fn(s);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

// ---------- DeviceKey lifecycle ----------

export function setDeviceKey(key: Uint8Array): void { deviceKeyRef = key; }
export function clearDeviceKey(): void { deviceKeyRef = null; }
export function hasDeviceKey(): boolean { return deviceKeyRef !== null; }

function requireDeviceKey(): Uint8Array {
  if (!deviceKeyRef) throw new Error('device locked: no deviceKey set');
  return deviceKeyRef;
}

export function deviceEncrypt(plain: Uint8Array): Uint8Array {
  const key = requireDeviceKey();
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const ct = sodium.crypto_secretbox_easy(plain, nonce, key);
  const out = new Uint8Array(nonce.length + ct.length);
  out.set(nonce, 0);
  out.set(ct, nonce.length);
  return out;
}

export function deviceDecrypt(blob: Uint8Array): Uint8Array {
  const key = requireDeviceKey();
  const nonce = blob.slice(0, sodium.crypto_secretbox_NONCEBYTES);
  const ct = blob.slice(sodium.crypto_secretbox_NONCEBYTES);
  return sodium.crypto_secretbox_open_easy(ct, nonce, key);
}

const encrypt = deviceEncrypt;
const decrypt = deviceDecrypt;

// ---------- KV (meta del device, sin cifrar — meta operacional) ----------

export async function kvGet(key: string): Promise<unknown> {
  return withStore(STORE_KV, 'readonly', (s) => s.get(key));
}
export async function kvSet(key: string, value: unknown): Promise<void> {
  await withStore(STORE_KV, 'readwrite', (s) => s.put(value, key));
}
export async function kvDelete(key: string): Promise<void> {
  await withStore(STORE_KV, 'readwrite', (s) => s.delete(key));
}

// ---------- Outbox (cifrado con deviceKey) ----------

export type OutboxStatus = 'pending' | 'sent' | 'failed';

export interface OutboxEntry {
  id: string;
  messageId: string;
  freqLabel: string;
  receiverSyphraxId: string;
  receiverDeviceIds: string[];     // legacy compat — no se usa en modelo simple
  selfDeviceIds: string[];         // legacy compat — no se usa en modelo simple
  type: 'msg' | 'media' | 'ctrl';
  plaintext: string;
  createdAt: string;
  status: OutboxStatus;
  retries: number;
  nextRetryAt: string;
  serverIds?: Record<string, string>;
  error?: string;
}

interface OutboxStored {
  id: string;
  status: OutboxStatus;
  createdAt: string;
  cipher: Uint8Array;
}

export async function outboxPut(entry: OutboxEntry): Promise<void> {
  const json = new TextEncoder().encode(JSON.stringify(entry));
  const cipher = encrypt(json);
  const stored: OutboxStored = { id: entry.id, status: entry.status, createdAt: entry.createdAt, cipher };
  await withStore(STORE_OUTBOX, 'readwrite', (s) => s.put(stored));
}

export async function outboxGet(id: string): Promise<OutboxEntry | null> {
  const stored = await withStore<OutboxStored | undefined>(STORE_OUTBOX, 'readonly', (s) => s.get(id));
  if (!stored) return null;
  const cipher = stored.cipher instanceof ArrayBuffer ? new Uint8Array(stored.cipher) : stored.cipher;
  const plain = decrypt(cipher);
  return JSON.parse(new TextDecoder().decode(plain)) as OutboxEntry;
}

export async function outboxDelete(id: string): Promise<void> {
  await withStore(STORE_OUTBOX, 'readwrite', (s) => s.delete(id));
}

export async function outboxListPending(): Promise<OutboxEntry[]> {
  const all = await withStore<OutboxStored[]>(STORE_OUTBOX, 'readonly', (s) => {
    const idx = s.index('status');
    return idx.getAll('pending');
  });
  const out: OutboxEntry[] = [];
  for (const stored of all) {
    try {
      const cipher = stored.cipher instanceof ArrayBuffer ? new Uint8Array(stored.cipher) : stored.cipher;
      const plain = decrypt(cipher);
      out.push(JSON.parse(new TextDecoder().decode(plain)) as OutboxEntry);
    } catch (_e) { /* skip corrupt */ }
  }
  return out.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

// ---------- Snapshot del historial (cifrado con deviceKey) ----------

export async function saveSnapshot(syphraxId: string, snapshot: unknown): Promise<void> {
  const json = new TextEncoder().encode(JSON.stringify(snapshot));
  const enc = deviceEncrypt(json);
  await kvSet('snapshot:' + syphraxId, enc);
}

export async function loadSnapshot(syphraxId: string): Promise<unknown | null> {
  const blob = await kvGet('snapshot:' + syphraxId);
  if (!(blob instanceof Uint8Array) && !(blob instanceof ArrayBuffer)) return null;
  const bytes = blob instanceof ArrayBuffer ? new Uint8Array(blob) : blob;
  try {
    const plain = deviceDecrypt(bytes);
    return JSON.parse(new TextDecoder().decode(plain));
  } catch (_e) {
    return null;
  }
}

// ---------- Inbox cursor (no cifrado: solo timestamp ISO) ----------

export async function getCursor(key: string): Promise<string | null> {
  const v = await withStore<unknown>(STORE_INBOX_CURSOR, 'readonly', (s) => s.get(key));
  return typeof v === 'string' ? v : null;
}

export async function setCursor(key: string, value: string): Promise<void> {
  await withStore(STORE_INBOX_CURSOR, 'readwrite', (s) => s.put(value, key));
}

// ---------- Bootstrap helpers ----------

export async function deriveDeviceKey(pin: string, salt: Uint8Array): Promise<Uint8Array> {
  await crypto.ready();
  return sodium.crypto_pwhash(
    32,
    pin,
    salt,
    sodium.crypto_pwhash_OPSLIMIT_MODERATE,
    sodium.crypto_pwhash_MEMLIMIT_MODERATE,
    sodium.crypto_pwhash_ALG_ARGON2ID13,
  );
}

export async function getOrCreateDeviceSalt(): Promise<Uint8Array> {
  const stored = await kvGet('deviceSalt');
  if (stored instanceof Uint8Array) return stored;
  if (stored instanceof ArrayBuffer) return new Uint8Array(stored);
  await crypto.ready();
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  await kvSet('deviceSalt', salt);
  return salt;
}

export async function wipeAll(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction([STORE_KV, STORE_RATCHET, STORE_OUTBOX, STORE_INBOX_CURSOR], 'readwrite');
    tx.objectStore(STORE_KV).clear();
    tx.objectStore(STORE_RATCHET).clear();
    tx.objectStore(STORE_OUTBOX).clear();
    tx.objectStore(STORE_INBOX_CURSOR).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  deviceKeyRef = null;
}

// ---------- Legacy compat shims (ratchet store ya no se usa) ----------
// Mantenidos como no-op para no romper imports del código viejo durante la migración.

export interface SerializedSession {
  rootKey: string;
  sendChainKey: string | null;
  recvChainKey: string | null;
  myRatchetPriv: string | null;
  myRatchetPub: string | null;
  theirRatchetPub: string | null;
  sendCounter: number;
  recvCounter: number;
  prevSendCounter: number;
  skipped: Record<string, string>;
  needToRotateSendKey?: boolean;
}

export async function loadSession(_peerKey: string): Promise<SerializedSession | null> { return null; }
export async function saveSession(_peerKey: string, _session: SerializedSession): Promise<void> { /* no-op */ }
export async function deleteSession(_peerKey: string): Promise<void> { /* no-op */ }
export async function listSessionKeys(): Promise<string[]> { return []; }
